import type { Contact, ContactSide, Decision, Meeting, MonthClose, MonthReviewData, RelationshipData, ReviewData, SearchResults, ShareLink, BriefTemplate, ReorderValues, AccountRecord, Feedback, FeedbackRating, Handoff, Insights, AccountSummary, ActivityItem, AdminUser, AgentKey, Attachment, Delivery, DashboardData, NewClient, NewUser, PortfolioClient, RateOverride, Service, SessionUser, TimelineEntry, UserRole } from "./types";

export class ApiError extends Error {
  status: number;
  /** The refusal body itself. A route that can explain WHY it said no — a contract
   *  window, a quota — puts the reason here, so the screen can say something true
   *  instead of spinning forever. */
  detail: Record<string, unknown>;
  constructor(message: string, status: number, detail: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(payload.error || "Request failed.", response.status, payload);
  return payload as T;
}

export const dashboardApi = {
  config: () => api<{ demoEnabled: boolean; monthlyCredits: number; creditValueKd: number; approvalThreshold: number | null }>("/api/config"),
  session: () => api<{ user: SessionUser | null; accounts?: AccountSummary[]; activeAccount?: AccountSummary | null }>("/api/session"),
  deliveries: (month: string) => api<{ month: string; deliveries: Delivery[] }>(`/api/deliveries?month=${month}`),
  attachments: (requestId: string) => api<{ attachments: Attachment[] }>(`/api/requests/${requestId}/attachments`),
  search: (q: string) => api<SearchResults>(`/api/search?q=${encodeURIComponent(q)}`),
  templates: () => api<{ templates: BriefTemplate[] }>("/api/templates"),
  template: (id: string) => api<BriefTemplate>(`/api/templates/${id}`),
  saveTemplate: (body: {
    name: string; serviceId: string; title: string; brief: string;
    quantity: number; language: string; channel: string; referenceUrl: string;
  }) => api<BriefTemplate>("/api/templates", { method: "POST", body: JSON.stringify(body) }),
  deleteTemplate: (id: string) => api<{ ok: true }>(`/api/templates/${id}`, { method: "DELETE" }),
  reorder: (requestId: string) => api<ReorderValues>(`/api/requests/${requestId}/reorder`),
  timeline: (requestId: string) => api<{ timeline: TimelineEntry[] }>(`/api/requests/${requestId}/timeline`),
  insights: (month: string) => api<Insights>(`/api/insights?month=${month}`),
  handoff: (requestId: string) => api<Handoff>(`/api/requests/${requestId}/handoff`),
  sendFeedback: (requestId: string, rating: FeedbackRating, note?: string) =>
    api<{ ok: true; feedback: Feedback[] }>(`/api/requests/${requestId}/feedback`, {
      method: "POST", body: JSON.stringify({ rating, note }),
    }),
  uploadAttachments: async (requestId: string, files: FileList, kind: "brief" | "deliverable") => {
    // FormData, so no Content-Type header — the browser must set the multipart
    // boundary itself, and our api() helper would override it with JSON.
    const body = new FormData();
    body.append("kind", kind);
    for (const file of Array.from(files)) body.append("files", file);
    const response = await fetch(`/api/requests/${requestId}/attachments`, { method: "POST", body });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(payload.error || "Upload failed.", response.status);
    return payload as { attachments: Attachment[] };
  },
  deleteAttachment: (id: string) => api<{ ok: true }>(`/api/attachments/${id}`, { method: "DELETE" }),
  portfolio: (month: string) => api<{ month: string; clients: PortfolioClient[] }>(`/api/admin/portfolio?month=${month}`),
  rates: () => api<{ rates: RateOverride[] }>("/api/admin/rates"),
  setRate: (serviceId: string, credits: number | null, note?: string) =>
    api<{ ok: true }>("/api/admin/rates", { method: "PUT", body: JSON.stringify({ serviceId, credits, note }) }),
  clients: () => api<{ clients: AccountRecord[] }>("/api/admin/clients"),
  createClient: (client: NewClient) =>
    api<{ client: AccountRecord }>("/api/admin/clients", { method: "POST", body: JSON.stringify(client) }),
  adminUsers: () => api<{ users: AdminUser[]; clients: AccountSummary[] }>("/api/admin/users"),
  agentKeys: () => api<{ keys: AgentKey[] }>("/api/admin/agent-keys"),
  createAgentKey: (input: { name: string; platform: boolean; actsAsEmail?: string }) =>
    api<{ id: string; key: string }>("/api/admin/agent-keys", { method: "POST", body: JSON.stringify(input) }),
  revokeAgentKey: (id: string) => api<{ ok: true }>(`/api/admin/agent-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setUserAccounts: (userId: string, accountIds: string[]) =>
    api<{ assignedAccountIds: string[] }>(`/api/admin/users/${encodeURIComponent(userId)}/accounts`, {
      method: "PUT",
      body: JSON.stringify({ accountIds }),
    }),
  createUser: (user: NewUser) =>
    api<{ email: string; password: string; invited?: boolean }>("/api/admin/users", { method: "POST", body: JSON.stringify(user) }),
  updateUser: (id: string, change: { role?: UserRole; active?: boolean; resetPassword?: boolean }) =>
    api<{ ok: true; password?: string }>(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(change) }),
  // emailOptIn is omitted, not sent as false, when there is nothing to answer —
  // the server treats an absent field as "leave the existing choice alone".
  changePassword: (currentPassword: string, newPassword: string, emailOptIn?: boolean) =>
    api<{ ok: true; user: SessionUser }>("/api/auth/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword, ...(emailOptIn === undefined ? {} : { emailOptIn }) }),
    }),
  switchAccount: (accountId: string) =>
    api<{ user: SessionUser; account: AccountSummary }>("/api/session/account", {
      method: "POST",
      body: JSON.stringify({ accountId }),
    }),
  login: (email: string, password: string) =>
    api<{ user: SessionUser }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  preview: (role: UserRole) =>
    api<{ user: SessionUser }>("/api/auth/demo", { method: "POST", body: JSON.stringify({ role }) }),
  logout: () => api<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  dashboard: (month: string) => api<DashboardData>(`/api/dashboard?month=${encodeURIComponent(month)}`),
  services: () => api<{ services: Service[]; categories: string[]; categoriesAr?: Record<string, string | null> }>("/api/services"),
  createRequest: (payload: Record<string, unknown>) =>
    api<{ id: string; estimatedCredits: number }>("/api/requests", { method: "POST", body: JSON.stringify(payload) }),
  updateRequest: (id: string, payload: Record<string, unknown>) =>
    api<{ ok: true }>(`/api/requests/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  logDelivery: (payload: Record<string, unknown>) =>
    api<{ id: string; credits: number }>("/api/admin/deliveries", { method: "POST", body: JSON.stringify(payload) }),
  addCredits: (payload: Record<string, unknown>) =>
    api<{ id: string }>("/api/admin/credits", { method: "POST", body: JSON.stringify(payload) }),
  activity: () => api<{ activity: ActivityItem[] }>("/api/activity"),

  // The record of the relationship (backlog E1-E6). One read for the whole screen;
  // the pieces are small and always shown together, so five round trips would buy
  // nothing but five chances to render half a page.
  relationship: () => api<RelationshipData>("/api/relationship"),
  saveContact: (id: string | null, body: {
    side: ContactSide; name: string; title: string; email: string; phone: string; isApprover: boolean; note: string;
  }) => api<{ contact: Contact }>(id ? `/api/relationship/contacts/${id}` : "/api/relationship/contacts",
    { method: id ? "PATCH" : "POST", body: JSON.stringify(body) }),
  deleteContact: (id: string) => api<{ ok: true }>(`/api/relationship/contacts/${id}`, { method: "DELETE" }),
  saveMeeting: (id: string | null, body: { heldOn: string; title: string; attendees: string; summary: string }) =>
    api<{ meeting: Meeting }>(id ? `/api/relationship/meetings/${id}` : "/api/relationship/meetings",
      { method: id ? "PATCH" : "POST", body: JSON.stringify(body) }),
  deleteMeeting: (id: string) => api<{ ok: true }>(`/api/relationship/meetings/${id}`, { method: "DELETE" }),
  saveDecision: (id: string | null, body: {
    decidedOn: string; title: string; rationale: string; requestId: string | null; meetingId: string | null;
  }) => api<{ decision: Decision }>(id ? `/api/relationship/decisions/${id}` : "/api/relationship/decisions",
    { method: id ? "PATCH" : "POST", body: JSON.stringify(body) }),
  deleteDecision: (id: string) => api<{ ok: true }>(`/api/relationship/decisions/${id}`, { method: "DELETE" }),
  uploadContractDocuments: async (files: FileList) => {
    // FormData, so no Content-Type header — the browser must set the multipart
    // boundary itself, and api() would override it with JSON.
    const body = new FormData();
    for (const file of Array.from(files)) body.append("files", file);
    const response = await fetch("/api/relationship/documents", { method: "POST", body });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(payload.error || "Upload failed.", response.status);
    return payload as { documents: Attachment[] };
  },
  monthReview: (month: string) => api<MonthReviewData>(`/api/month-review?month=${encodeURIComponent(month)}`),
  review: (quarter: string) => api<ReviewData>(`/api/review?quarter=${encodeURIComponent(quarter)}`),

  // Closing a month (BM ruling). The preview is what a manager approves; the close
  // writes that same figure, so the two can never show different numbers.
  shareLinks: (requestId: string) => api<{ links: ShareLink[] }>(`/api/requests/${requestId}/share`),
  createShareLink: (requestId: string, days?: number) =>
    api<{ link: ShareLink }>(`/api/requests/${requestId}/share`, { method: "POST", body: JSON.stringify({ days }) }),
  revokeShareLink: (token: string) => api<{ ok: true }>(`/api/share/${token}`, { method: "DELETE" }),

  monthClose: (month: string) => api<{ close: MonthClose }>(`/api/months/${month}/close`),
  closeMonth: (month: string, note: string) =>
    api<{ close: MonthClose }>(`/api/months/${month}/close`, { method: "POST", body: JSON.stringify({ note }) }),
  reopenMonth: (month: string) =>
    api<{ close: MonthClose }>(`/api/months/${month}/close`, { method: "DELETE" }),
};
